import update from 'immutability-helper';
import _, { omit } from 'lodash';
import { ProductStatus, ZakekeEnvironment } from 'src/shared/enums';
import { toGlobalID, uuid } from 'src/shared/helpers';
import { RelayFragment } from 'src/shared/types';
import { ProductProvider } from '../../steps/1-addProduct/__generated__/addProductQuery.graphql';
import { ColorAttribute, matchColorsAttributes } from '../../steps/4-variants/helpers';
import { merchantProduct_item$data } from '../queries/__generated__/merchantProduct_item.graphql';
import { productFragment_model$data } from '../queries/__generated__/productFragment_model.graphql';
import SelectedOptionValue from '../selectedOptionValue';
import { Side } from '../side';
import { ModelArea, ModelAreaLayer } from './modelArea';
import { ModelColor, createDefaultModelColor } from './modelColor';
import { AdditionalCode, ModelColorMapping } from './modelColorMapping';
import { ModelOptions } from './modelOptions';
import { ModelSceneConfig } from './modelScenes';
import { ModelSide, createDefaultModelSide } from './modelSide';

/**
 * Product configuration container. Will keep the configured product hiearchy.
 */
export interface Model {
    colors: ModelColor[];
    id: string | null;
    isDemo: boolean;
    statusID: ProductStatus;
    configurationStatus: productFragment_model$data['configurationStatus'];
    name: string;
    providerCode: string | null;
    code: string;
    imageUrl: string | null;
    pricingModelID: number;
    environment: ZakekeEnvironment;
    provider: ProductProvider;
    options: ModelOptions[];
    colorMappings: ModelColorMapping[];
    additionalCodes: AdditionalCode[];
    sceneConfig: ModelSceneConfig | null;
}

/**
 * Create a new empty Model
 * @param code A product code from the store
 * @param basePrintType An original printType used to add initial content
 * @param baseSide An original side used to add initial content
 */
export function createEmpty(baseSide?: Side) {
    const product: Model = {
        colors: [],
        id: null,
        isDemo: false,
        statusID: ProductStatus.UnPublished,
        configurationStatus: 'NONE',
        name: '',
        providerCode: null,
        code: '',
        imageUrl: null,
        pricingModelID: 3,
        environment: ZakekeEnvironment.Customizer,
        provider: 'ZAKEKE',
        options: [],
        colorMappings: [],
        additionalCodes: [],
        sceneConfig: null
    };
    if (baseSide) product.colors.push(createDefaultModelColor(baseSide));

    return product;
}

export function setMerchantProduct(
    product: Model,
    merchantProduct: Omit<RelayFragment<merchantProduct_item$data>, ' $fragmentType'>,
    customThumbnail: HTMLImageElement | null
) {
    const newProduct = update(product, {
        $merge: {
            code: merchantProduct.sku || '',
            name: merchantProduct.name || '',
            imageUrl: customThumbnail?.src
        }
    });

    if (customThumbnail) {
        for (const color of product.colors) {
            color.sides = color.sides.map((x) => {
                return update(x, {
                    $merge: {
                        imageID: null,
                        imageUrl: customThumbnail!.src
                    }
                });
            });
        }
    }

    return newProduct;
}

export function setProductImageNoEcommerce(product: Model, imageUrl: string) {
    const newProduct = update(product, {
        $merge: {
            imageUrl
        }
    });

    for (const color of product.colors) {
        color.sides = color.sides.map((x) => {
            return update(x, {
                $merge: {
                    imageID: null,
                    imageUrl
                }
            });
        });
    }

    return newProduct;
}

export function setProductProviderCode(product: Model, providerCode: string | null) {
    const newProduct = update(product, {
        providerCode: { $set: providerCode }
    });
    return newProduct;
}

/**
 * Create a Model from query model.
 * The original sides collection must be build before with {@link Side#getAllSidesFromQueryModel} that will let
 * keep track of the ModelSide <=> Side relationship.
 *
 * @param model The model received from server
 * @param sides The original sides list used to keep track of model sides
 */
export function createModelFromDto(model: Omit<RelayFragment<productFragment_model$data>, ' $fragmentType'>, sides: Side[]) {
    const product: Model = {
        colors: [],
        id: model.id,
        isDemo: model.isDemo,
        statusID: model.statusID,
        configurationStatus: model.configurationStatus,
        name: model.name,
        providerCode: model.providerCode,
        code: model.code,
        imageUrl: model.smallImageUrl,
        pricingModelID: model.pricingModelID,
        environment: ZakekeEnvironment.Customizer,
        provider: model.provider,
        options: [],
        colorMappings: [...(model.modelColorMappings ?? [])],
        additionalCodes: (model.additionalCodes ?? []).map((ac) => {
            return {
                code: ac.code,
                salesChannelId: ac.salesChannelID,
                remoteUrl: ac.salesChannel?.ecommerceSettings?.remoteUrl,
                merchantProductName: ac.merchantProductName
            };
        }),
        sceneConfig: model.sceneConfig
            ? {
                  general: model.sceneConfig.general
                      ? {
                            arAnchor: model.sceneConfig.general.arAnchor,
                            arEnabled: model.sceneConfig.general.arEnabled
                        }
                      : null
              }
            : null
    };

    const sortedColors = _.sortBy(model.colors ?? [], (color) => color.position);

    for (const modelColor of sortedColors) {
        const color: ModelColor = {
            colorID: modelColor.colorID,
            name: modelColor.name ?? '',
            code: modelColor.code ?? '',
            colorProviderCode: modelColor.colorProviderCode,
            position: modelColor.position || sortedColors.indexOf(modelColor), // indexOf used to fix old colors without a defined position
            sides: [],
            guid: uuid()
        };

        product.colors.push(color);

        for (const modelSide of modelColor.sides ?? []) {
            const originalSide = sides.find((side) => side.sideID == modelSide.sideID);

            if (!originalSide) continue;

            const side: ModelSide = {
                sideID: modelSide.sideID,
                originalSideGuid: originalSide.guid, // Keep track of the real side
                imageID: 1,
                imageUrl: modelSide.imageUrl!,
                ppcm: modelSide.ppcm,
                code: modelSide.code,
                sortOrder: modelSide.sortOrder ?? sides.indexOf(originalSide),
                areas: [],
                tempImageFileKey: '',
                guid: uuid(),
                mandatory: modelSide.mandatory || false,
                canRotate: modelSide.canRotate || false,
                cameraLocationID: modelSide.cameraLocationID || null
            };

            color.sides.push(side);

            for (const modelArea of modelSide.areas ?? []) {
                const area: ModelArea = {
                    areaID: modelArea.areaID,
                    boundsX: modelArea.boundsX,
                    boundsY: modelArea.boundsY,
                    boundsWidth: modelArea.boundsWidth,
                    boundsHeight: modelArea.boundsHeight,
                    path: modelArea.path,
                    pathSVG: modelArea.pathSVG,
                    guid: modelArea.guid,
                    clipOut: modelArea.clipOut,
                    name: modelArea.name,
                    layers: [],
                    templateFile: modelArea.templateFile
                        ? {
                              fileID: null,
                              fileObjectID: modelArea.templateFile.fileObjectID,
                              documentWidth: modelArea.templateFile.documentWidth,
                              documentHeight: modelArea.templateFile.documentHeight
                          }
                        : null
                };

                side.areas.push(area);

                for (const areaLayer of modelArea.layers ?? []) {
                    const layer: ModelAreaLayer = {
                        id: toGlobalID((areaLayer as any).layerID, 'AreaLayer'),
                        name: areaLayer.name,
                        displayName: areaLayer.displayName,
                        description: areaLayer.description,
                        svg: areaLayer.svg,
                        json: areaLayer.json,
                        displayOrder: areaLayer.displayOrder,
                        isVisibleInCanvas: areaLayer.isVisibleInCanvas,
                        isVisibleInCart: areaLayer.isVisibleInCart,
                        isVisibleInOutput: areaLayer.isVisibleInOutput,
                        showLabel: areaLayer.showLabel,
                        isClipMask: areaLayer.isClipMask,
                        isOutputClipped: areaLayer.isOutputClipped,
                        showIn3DPreview: areaLayer.showIn3DPreview
                    };

                    area.layers.push(layer);
                }
            }
        }
    }

    for (const modelOption of model.options ?? []) {
        const modelColor = product.colors.find((x) => x.colorID === modelOption.colorID);
        const modelSide = modelColor?.sides.find((x) => x.sideID === modelOption.sideID);
        const optionsData = modelOption.optionsData;

        if (!modelColor || !optionsData) continue;

        const option: ModelOptions = {
            optionID: modelOption.optionID,
            colorGuid: modelColor!.guid,
            sideGuid: modelSide?.guid ?? null,
            isResizable: optionsData.isResizable,
            is3DAutoGenEnabled: optionsData.is3DAutoGenEnabled,
            isSplitOutput: optionsData.isSplitOutput,
            startWidth: optionsData.startWidth,
            minWidth: optionsData.minWidth,
            maxWidth: optionsData.maxWidth,
            stepWidth: optionsData.stepWidth,
            startHeight: optionsData.startHeight,
            minHeight: optionsData.minHeight,
            maxHeight: optionsData.maxHeight,
            shape: optionsData.shape ?? 0, // Default to rectangle if not set
            stepHeight: optionsData.stepHeight,
            maxSizeForSide: optionsData.maxSizeForSide,
            minArea: optionsData.minArea,
            maxArea: optionsData.maxArea,
            depth: optionsData.depth,
            keepAspectRatio: optionsData.keepAspectRatio,
            lockWidth: optionsData.lockWidth,
            lockHeight: optionsData.lockHeight,
            showWidthSize: optionsData.showWidthSize,
            showHeightSize: optionsData.showHeightSize,
            showSplitGrid: optionsData.showSplitGrid,
            mirrorType: optionsData.mirrorType,
            edgesColor: optionsData.edgesColor,
            minSplitWidth: optionsData.minSplitWidth,
            minSplitHeight: optionsData.minSplitHeight,
            startSplitColumns: optionsData.startSplitColumns,
            startSplitRows: optionsData.startSplitRows,
            includeBorderInPrintFiles: optionsData.includeBorderInPrintFiles,
            backgroundType: optionsData.backgroundType ?? null,
            backgroundColor: optionsData.backgroundColor ?? null,
            backgroundPresetID: optionsData.backgroundPresetID ?? 0,
            backgroundImageTempUrl: optionsData.backgroundImageTempUrl ?? null,
            backgroundImageFileObjectID: optionsData.backgroundImageFileObjectID ?? null,
            backgroundImageThumbnailFileObjectID: optionsData.backgroundImageThumbnailFileObjectID ?? null,
            backgroundImageUrl: optionsData.backgroundImageUrl ?? null,
            backgroundImageThumbnailUrl: optionsData.backgroundImageThumbnailUrl ?? null,
            presets:
                optionsData.presets && optionsData.presets.length > 0
                    ? optionsData.presets.map((preset) => {
                          return {
                              name: preset?.name ?? '',
                              width: preset?.width ?? 0,
                              height: preset?.height ?? 0
                          };
                      })
                    : null
        };

        product.options.push(option);
    }

    return product;
}

/**
 * Add a new side with a placeholder area.
 * To add an existing side, please use {@link addSide}
 */
export function addSide(product: Model, side: Side) {
    let newProduct = product;

    for (const colorIndex in product.colors) {
        if ((product.colors as Object).hasOwnProperty(colorIndex))
            // Fix the .remove metod helper class in the backoffice
            newProduct = addEmptyModelSide(newProduct, newProduct.colors[colorIndex], side);
    }

    return newProduct;
}

// Remove a side from each color
export function removeSide(product: Model, side: Side) {
    let newProduct = product;

    for (const colorIndex in product.colors) {
        if ((product.colors as Object).hasOwnProperty(colorIndex)) {
            // Fix the .remove metod helper class in the backoffice

            const modelColor = product.colors[colorIndex];
            const modelSide = modelColor.sides.find((x) => x.originalSideGuid == side.guid);

            if (modelSide) {
                const modelSideIndex = modelColor.sides.indexOf(modelSide);
                newProduct = update(newProduct, { colors: { [colorIndex]: { sides: { $splice: [[modelSideIndex, 1]] } } } });
            }
        }
    }

    return newProduct;
}

export const mergeModelSide = (product: Model, modelSide: ModelSide, newModelSide: Partial<ModelSide>) => {
    const modelColor = findModelColorForModelSide(product, modelSide);

    if (modelColor) {
        const _modelSide = modelColor.sides.find((x) => x.guid == modelSide.guid);

        if (_modelSide) {
            const colorIndex = product.colors.indexOf(modelColor);
            const sideIndex = modelColor.sides.indexOf(_modelSide);

            // Merge but keep the guids correctly
            return update(product, {
                colors: {
                    [colorIndex]: {
                        sides: {
                            [sideIndex]: {
                                $merge: {
                                    ...newModelSide,
                                    guid: modelSide.guid
                                },
                                areas: {
                                    $set:
                                        newModelSide.areas?.map((area) => {
                                            const previousArea = modelSide.areas.find((x) => x.guid === area.guid);

                                            return {
                                                ...area,
                                                guid: previousArea ? previousArea.guid : uuid(),
                                                layers: [...area.layers]
                                            };
                                        }) ?? modelSide.areas
                                }
                            }
                        }
                    }
                }
            });
        }
    }

    return product;
};

export const addEmptyModelSide = (product: Model, modelColor: ModelColor, side: Side) => {
    const colorIndex = product.colors.indexOf(modelColor);
    const sidesLength = getAllModelSides(product).length;

    if (!product.colors[colorIndex].sides.find((x) => x.originalSideGuid == side.guid)) {
        const modelSide = createDefaultModelSide(side);
        modelSide.sortOrder = sidesLength;

        if (product.colors[colorIndex].sides.length > 0) {
            modelSide.imageID = null;
            modelSide.imageUrl = product.colors[colorIndex].sides[0].imageUrl;
        }

        // Check if the variant is resizable
        const modelOptions = product.options.find((x) => x.colorGuid == modelColor.guid && x.isResizable);
        if (modelColor.sides.length > 0 && modelOptions && modelOptions.isResizable) {
            // Add a modelArea the same as the first side of the same color
            const firstModelArea = modelColor.sides[0].areas[0];

            if (firstModelArea) {
                const modelArea = { ...firstModelArea };
                modelArea.guid = uuid();
                modelSide.areas.push(modelArea);
                modelSide.ppcm = modelColor.sides[0].ppcm;
            }
        }

        return update(product, { colors: { [colorIndex]: { sides: { $push: [modelSide] } } } });
    } else {
        return product;
    }
};

export const removeModelSide = (product: Model, modelSide: ModelSide) => {
    const modelColor = findModelColorForModelSide(product, modelSide);

    if (modelColor) {
        const _modelSide = modelColor.sides.find((x) => x.guid == modelSide.guid);

        if (_modelSide) {
            const colorIndex = product.colors.indexOf(modelColor);
            const sideIndex = modelColor.sides.indexOf(_modelSide);

            return update(product, {
                colors: {
                    [colorIndex]: {
                        sides: {
                            $splice: [[sideIndex, 1]]
                        }
                    }
                }
            });
        }
    }

    return product;
};

export const upsertModelOptions = (product: Model, options: ModelOptions) => {
    let newProduct = { ...product };
    const existingOptionIndex = newProduct.options.findIndex((x) => x.colorGuid === options.colorGuid && x.sideGuid === options.sideGuid);

    if (existingOptionIndex !== -1) {
        newProduct = update(newProduct, { options: { [existingOptionIndex]: { $set: options } } });
    } else {
        newProduct = update(newProduct, { options: { $push: [options] } });
    }

    return newProduct;
};

export const removeModelOptionForColor = (product: Model, modelColor: ModelColor) => {
    const newOptions = product.options.filter((x) => x.colorGuid !== modelColor.guid);

    return update(product, { options: { $set: newOptions } });
};

/**
 * Add a new color by copying sides and areas from the first color if available.
 * Remove the sample color if preset.
 * To add an existing color, please use {@link addColor}
 */
export function addNewColor(product: Model, optionsValues: SelectedOptionValue[], mainSideImageUrl: string | null = null) {
    if (product.colors.find((x) => matchColorsAttributes(x.code, optionsValues))) throw new Error('Color with this code already exists');

    const name = optionsValues.map((x) => x.Value.Label).join(' - ');
    const code = JSON.stringify(optionsValues);

    const color: ModelColor = {
        colorID: null,
        name,
        code,
        colorProviderCode: null,
        position: product.colors.length,
        guid: uuid(),
        sides: []
    };

    if (product.colors.length > 0) {
        const clonedSideGuids = new Map<string, string>();

        for (const modelSide of product.colors[0].sides) {
            const clonedSideGuid = uuid();
            clonedSideGuids.set(modelSide.guid, clonedSideGuid);

            const cloneSide = {
                ...modelSide,
                guid: clonedSideGuid,
                areas: [
                    ...modelSide.areas.map((area) => ({
                        ...area,
                        guid: uuid(),
                        layers: area.layers.map((y) => ({
                            ...y,
                            id: toGlobalID(uuid(), 'AreaLayer')
                        }))
                    }))
                ]
            };

            color.sides.push(cloneSide);
        }

        // Copy resizable options, ecc
        const options = product.options.find((x) => x.colorGuid == product.colors[0].guid);

        if (options) {
            const newOptions = {
                ...options,
                colorGuid: color.guid,
                sideGuid: options.sideGuid ? clonedSideGuids.get(options.sideGuid) ?? options.sideGuid : null
            };

            product = upsertModelOptions(product, newOptions);
        }
    }

    if (mainSideImageUrl) {
        // If the main side image is provided, set it as the image for the first side
        if (color.sides.length > 0) {
            color.sides[0].imageUrl = mainSideImageUrl;
            color.sides[0].imageID = null; // Reset imageID to null if we are using a custom image
        }
    }

    const newProduct = update(product, {
        colors: {
            $push: [color]
        }
    });

    return newProduct;
}

export function addColor(product: Model, color: ModelColor) {
    const newProduct = update(product, { colors: { $push: [color] } });
    return newProduct;
}

export function removeColor(product: Model, color: ModelColor) {
    const newColors = product.colors.filter((x) => x.guid != color.guid);
    const newOptions = product.options.filter((x) => x.colorGuid != color.guid);
    const newProduct = update(product, { colors: { $set: newColors }, options: { $set: newOptions } });
    return newProduct;
}

export function editColorAttributes(product: Model, color: ModelColor, name: string, colorAttributes: ColorAttribute[]) {
    const index = product.colors.findIndex((x) => x.guid === color.guid);
    const jsonCode = JSON.stringify(colorAttributes);

    const newProduct = update(product, {
        colors: {
            [index]: {
                $merge: { name, code: jsonCode }
            }
        }
    });
    return newProduct;
}

export function editColorMapping(product: Model, colorMapping: ModelColorMapping) {
    const index = product.colorMappings.findIndex((x) => x.colorID === colorMapping.colorID);

    const newProduct =
        index >= 0
            ? update(product, {
                  colorMappings: {
                      [index]: {
                          $set: colorMapping
                      }
                  }
              })
            : update(product, {
                  colorMappings: {
                      $push: [colorMapping]
                  }
              });

    return newProduct;
}

export function editModelColorMappingsSalesChannels(product: Model, modelColorMappingsSalesChannels: ModelColorMapping[]) {
    const newProduct = update(product, {
        colorMappings: {
            $set: modelColorMappingsSalesChannels
        }
    });

    return newProduct;
}

export function changeColorName(product: Model, color: ModelColor, name: string) {
    const index = product.colors.findIndex((x) => x.guid === color.guid);
    const newColors = update(product.colors, {
        [index]: {
            $merge: { name }
        }
    });

    const newProduct = update(product, { colors: { $set: newColors } });
    return newProduct;
}

export function editColorJsonSettings(
    product: Model,
    color: ModelColor,
    settings: { colorProviderCode: string | null; sideCodes: { [sideGuid: string]: string } }
) {
    const colorIndex = product.colors.findIndex((x) => x.guid === color.guid);

    const newProduct = update(product, {
        colors: {
            [colorIndex]: {
                $merge: { colorProviderCode: settings.colorProviderCode }
            }
        }
    });

    // Update the side code for each specific side
    const updatedProduct = update(newProduct, {
        colors: {
            [colorIndex]: {
                sides: {
                    $apply: (sides: ModelSide[]) =>
                        sides.map((side) => ({
                            ...side,
                            code: settings.sideCodes[side.guid] || side.code || ''
                        }))
                }
            }
        }
    });

    return updatedProduct;
}

export function swapColorsPositions(product: Model, modelColor1: ModelColor, modelColor2: ModelColor) {
    const index1 = product.colors.indexOf(modelColor1);
    const index2 = product.colors.indexOf(modelColor2);

    product = update(product, {
        colors: {
            [index1]: {
                position: {
                    $set: index2
                }
            },
            [index2]: {
                position: {
                    $set: index1
                }
            }
        }
    });

    // Sort again the colors
    product = update(product, {
        colors: {
            $set: _.sortBy(product.colors, (color) => color.position)
        }
    });

    return product;
}

export const getAllModelSides = (product: Model) => {
    // Get the model sides because we need the images
    const modelSides: ModelSide[] = [];

    for (const modelColor of product.colors) {
        for (const modelSide of modelColor.sides) {
            if (!modelSides.find((x) => modelSide.originalSideGuid == x.originalSideGuid)) modelSides.push(modelSide);
        }
    }

    return modelSides.sort((a, b) => a.sortOrder - b.sortOrder);
};

export function sawpSides(product: Model, side1guid: string, side2guid: string) {
    // Get the indexes from allModelSides because it will contain for sure one model side per original side.
    // We can't take these index for each color because one or more model side can be missing.
    const allModelSides = getAllModelSides(product);
    const modelSide1 = allModelSides.find((x) => x.originalSideGuid == side1guid);
    const modelSide2 = allModelSides.find((x) => x.originalSideGuid == side2guid);

    if (modelSide1 && modelSide2) {
        const sortOrder1 = allModelSides.indexOf(modelSide1);
        const sortOrder2 = allModelSides.indexOf(modelSide2);

        for (const color of product.colors) {
            const _modelSide1 = color.sides.find((x) => x.originalSideGuid == modelSide1.originalSideGuid);
            const _modelSide2 = color.sides.find((x) => x.originalSideGuid == modelSide2.originalSideGuid);
            const colorIndex = product.colors.indexOf(color);
            const _modelSideIndex1 = _modelSide1 ? color.sides.indexOf(_modelSide1) : null;
            const _modelSideIndex2 = _modelSide2 ? color.sides.indexOf(_modelSide2) : null;

            console.log(_modelSideIndex1, _modelSideIndex2);
            if (_modelSideIndex1 != null) {
                product = update(product, {
                    colors: {
                        [colorIndex]: {
                            sides: {
                                [_modelSideIndex1]: {
                                    $merge: { sortOrder: sortOrder2 }
                                }
                            }
                        }
                    }
                });
            }

            if (_modelSideIndex2 != null) {
                product = update(product, {
                    colors: {
                        [colorIndex]: {
                            sides: {
                                [_modelSideIndex2]: {
                                    $merge: { sortOrder: sortOrder1 }
                                }
                            }
                        }
                    }
                });
            }
        }
    }
}

export function findModelColorForModelSide(product: Model, modelSide: ModelSide) {
    for (const _modelColor of product.colors) for (const _modelSide of _modelColor.sides) if (_modelSide.guid == modelSide.guid) return _modelColor;

    return null;
}

export function findModelColorByGuid(product: Model, guid: string) {
    for (const _modelColor of product.colors) if (_modelColor.guid == guid) return _modelColor;

    return null;
}

export function findModelSideByGuid(product: Model, guid: string) {
    for (const _modelColor of product.colors) for (const _modelSide of _modelColor.sides) if (_modelSide.guid == guid) return _modelSide;

    return null;
}

// Convert the object to a create mutation input
export function toUpdateMutationInput(productStatus: ProductStatus, salesChannelID: string, product: Model, sides: Side[]) {
    const isDemoDuplicate = product.isDemo;
    const duplicateColorGuids = new Map(product.colors.map((color) => [color.guid, uuid()]));
    const duplicateLogicalSideGuids = new Map(
        Array.from(new Set(product.colors.flatMap((color) => color.sides.map((side) => side.originalSideGuid)))).map(
            (logicalSideGuid) => [logicalSideGuid, uuid()] as const
        )
    );

    // A ModelSide GUID identifies the variant-specific copy in the editor. The API sideGuid,
    // instead, must identify the logical side shared by all variants. Keep the per-copy GUIDs
    // inside the UI and translate option references to the same logical GUID used by color sides.
    const getLogicalSideGuidForOption = (colorGuid: string, modelSideGuid: string | null) => {
        if (!modelSideGuid) return null;

        const modelSide =
            product.colors
                .find((color) => color.guid === colorGuid)
                ?.sides.find((side) => side.guid === modelSideGuid || side.originalSideGuid === modelSideGuid) ??
            product.colors
                .flatMap((color) => color.sides)
                .find((side) => side.guid === modelSideGuid || side.originalSideGuid === modelSideGuid);

        return modelSide?.originalSideGuid ?? modelSideGuid;
    };

    return {
        id: isDemoDuplicate ? null : product.id,
        name: { value: isDemoDuplicate ? `${product.name} (copy)` : product.name },
        providerCode: { value: isDemoDuplicate ? null : product.providerCode },
        code: { value: isDemoDuplicate ? `${product.code}-copy-${uuid().slice(0, 8)}` : product.code },
        salesChannelID: { value: salesChannelID },
        statusID: { value: productStatus },
        largeImageUrl: { value: product.imageUrl },
        environment: { value: product.environment },
        pricingModelID: { value: product.pricingModelID },
        options: {
            value: product.options.map((options) => {
                const logicalSideGuid = getLogicalSideGuidForOption(options.colorGuid, options.sideGuid);
                return {
                    optionID: isDemoDuplicate ? undefined : options.optionID!,
                    colorGuid: isDemoDuplicate ? duplicateColorGuids.get(options.colorGuid) ?? options.colorGuid : options.colorGuid,
                    sideGuid:
                        isDemoDuplicate && logicalSideGuid
                            ? duplicateLogicalSideGuids.get(logicalSideGuid) ?? logicalSideGuid
                            : logicalSideGuid,
                    optionsData: { ...omit(options, 'optionID', 'sideGuid', 'colorGuid'), presets: options.presets ?? [] }
                };
            })
        },
        sceneConfig: product.sceneConfig
            ? {
                  value: {
                      general: product.sceneConfig.general
                          ? {
                                arAnchor: product.sceneConfig.general.arAnchor,
                                arEnabled: product.sceneConfig.general.arEnabled
                            }
                          : null
                  }
              }
            : null,
        colors: {
            value: product.colors.map((color) => {
                return {
                    colorID: isDemoDuplicate ? null : color.colorID,
                    code: color.code,
                    colorProviderCode: color.colorProviderCode,
                    colorGuid: isDemoDuplicate ? duplicateColorGuids.get(color.guid)! : color.guid,
                    name: color.name,
                    position: color.position,
                    sides: color.sides.map((side) => {
                        const originalSide = sides.find((x) => x.guid == side.originalSideGuid);
                        const logicalSideGuid = side.originalSideGuid;
                        return {
                            sideID: isDemoDuplicate ? null : originalSide?.sideID,
                            sideGuid: isDemoDuplicate
                                ? duplicateLogicalSideGuids.get(logicalSideGuid) ?? logicalSideGuid
                                : logicalSideGuid,
                            name: originalSide?.name ?? '',
                            ppcm: side.ppcm,
                            code: side.code,
                            sortOrder: side.sortOrder,
                            imageUrl: side.imageUrl,
                            tempImageFileKey: side.tempImageFileKey,
                            mandatory: side.mandatory ? true : false,
                            canRotate: side.canRotate,
                            cameraLocationID: side.cameraLocationID,
                            areas: side.areas.map((area) => {
                                if (!isDemoDuplicate) return { ...area };
                                return {
                                    ...area,
                                    areaID: null,
                                    guid: uuid(),
                                    layers: area.layers.map((layer) => ({ ...layer, id: toGlobalID(uuid(), 'AreaLayer') })),
                                    templateFile: area.templateFile
                                        ? { ...area.templateFile, fileID: null, fileObjectID: null }
                                        : null
                                };
                            })
                        };
                    })
                };
            })
        }
    };
}

export function getProductIsResizable(product: Model): boolean {
    const color = product.colors.find((x) => x.colorID === 1);
    const options = product.options.filter((x) => x.colorGuid == color?.guid);
    if (options.length == 0) return false;

    return options[0].isResizable;
}

export function getResizableModelAttributes(product: Model): [ModelColor, ModelSide, ModelOptions | null] {
    const color = product.colors.find((x) => x.colorID === 1);
    if (!color) throw new Error('Model Color not found');

    const side = color?.sides[0];
    if (!side) throw new Error('Model Side not found');

    const options = product.options.filter((x) => x.colorGuid == color?.guid);
    const option = options.length > 0 ? options[0] : null;

    return [color, side, option];
}
